import { all, call, cancel, fork, put, select, spawn, take } from 'redux-saga/effects';
import { VISIBILITY_CHANGE } from '../../modules/app/actions';
import { visibilityChangeChannel } from '../../modules/app/channels';
import { getPathname } from '../../modules/app/selectors';
import { FAILURE as BETS_FAILURE, SUCCESS as BETS_SUCCESS } from '../../modules/bets/actions';
import betsRequest from '../../modules/bets/saga';
import { fetchCashInPrice } from '../../modules/cashInPrices/saga';
import { watchCashIn } from '../../modules/cimb/saga';
import watchInPlayChanges from '../../modules/livescore';
import { CASH_IN_PRICE_CHANGE } from '../../modules/pds/actions';
import watchPDSChangesV6 from '../../modules/pdsV6';
import { OPEN_BETS } from '../../routes/paths';
import { clearMessage, setMessage, SUCCESS } from '../FormMessage/actions';
import {
  LOAD_MORE,
  SET_CURRENT_BET,
  SET_NEXT_BET,
  SET_PREV_BET,
  setBets,
  setCurrentBet,
  setViewIsLoading,
  SHOW_CONGRATULATIONS,
  SHOW_LIST,
  updateViewState,
  VIEW_ENTER,
  VIEW_LEAVE,
  viewEnter,
  viewLeave
} from './actions';
import {
  countOpenBets,
  getCashedOutPrices,
  getCurrentBetId,
  getFirstBetId,
  getListVisible,
  getLiveEvents,
  getNextSlip,
  getPDSTopicParams,
  isCurrentBetIdValid,
  getCurrentBetFundingType
} from './selectors';

/* eslint-disable no-constant-condition */
export function* setupOpenBetsView(results, incremental) {
  yield put(setBets(results, incremental));

  const currentBetId = yield select(getCurrentBetId);
  const betsCount = yield select(countOpenBets);
  const listVisible = yield select(getListVisible);
  let newCurrentBetId = currentBetId;
  let newListVisible = listVisible;

  if (betsCount === 0) {
    newCurrentBetId = null;
  } else if (betsCount === 1) {
    newCurrentBetId = yield select(getFirstBetId);
    newListVisible = false;
  } else if (currentBetId) {
    const isValid = yield select(isCurrentBetIdValid);
    if (!isValid) {
      newCurrentBetId = null;
      newListVisible = true;
    }
  }
  if (newCurrentBetId !== currentBetId || newListVisible !== listVisible) {
    yield put(updateViewState(newCurrentBetId, newListVisible));
  }

  if (newListVisible === false && newCurrentBetId) {
    yield put(setCurrentBet(newCurrentBetId));
  }
}

export function* loadOpenBetsView(incremental) {
  if (incremental) {
    const nextSlip = yield select(getNextSlip);
    yield fork(betsRequest, null, null, null, nextSlip);
  } else {
    yield fork(betsRequest);
  }

  const { type, results } = yield take([BETS_SUCCESS, BETS_FAILURE]);
  if (type === BETS_SUCCESS) {
    yield call(setupOpenBetsView, results, incremental);
  }
  return type === BETS_SUCCESS;
}

export function* watchLoadMore() {
  while (yield take(LOAD_MORE)) {
    yield call(loadOpenBetsView, true);
  }
}

function* onDataLoaded() {
  const cimbTask = yield fork(watchCashIn);
  const loadMoreTask = yield fork(watchLoadMore);

  yield take(VIEW_LEAVE);
  cimbTask.cancel();
  loadMoreTask.cancel();
}

export function* watchViewEnter() {
  while (yield take(VIEW_ENTER)) {
    yield put(setViewIsLoading(true));
    if (yield call(loadOpenBetsView, false)) {
      yield fork(onDataLoaded);
    }
    yield put(setViewIsLoading(false));
  }
}

export function* watchWindowVisibility() {
  const channel = yield call(visibilityChangeChannel);
  while (true) {
    const { type, hidden } = yield take(channel);
    const path = yield select(getPathname);
    if (path === OPEN_BETS && type === VISIBILITY_CHANGE) {
      yield put((hidden) ? viewLeave() : viewEnter());
    }
  }
}

export function* liveUpdateCashInPrices() {
  const takeActions = [SET_CURRENT_BET, SET_NEXT_BET, SET_PREV_BET, CASH_IN_PRICE_CHANGE];
  while (yield take(takeActions)) {
    const betId = yield select(getCurrentBetId);
    yield call(fetchCashInPrice, betId);
  }
}

export function* watchCurrentBet() {
  let pdsTask;
  let inPlayTask;
  const takeActions = [SET_CURRENT_BET, SET_NEXT_BET, SET_PREV_BET, SHOW_LIST, VIEW_LEAVE];
  const actionsWithLiveChannels = [SET_CURRENT_BET, SET_NEXT_BET, SET_PREV_BET];
  while (true) {
    const { type } = yield take(takeActions);
    if (pdsTask) {
      yield cancel(pdsTask);
      pdsTask = null;
    }
    if (inPlayTask) {
      yield cancel(inPlayTask);
      inPlayTask = null;
    }
    if (actionsWithLiveChannels.includes(type)) {
      pdsTask = yield fork(watchPDSChangesV6, getPDSTopicParams);
      inPlayTask = yield fork(watchInPlayChanges, getLiveEvents);
    }
  }
}

export function* showCongratulations() {
  const actions = [SET_CURRENT_BET, SET_NEXT_BET, SET_PREV_BET, SHOW_LIST, SHOW_CONGRATULATIONS];
  while (yield take(actions)) {
    yield put(clearMessage('congratulations'));

    const currentBetId = yield select(getCurrentBetId);
    const cachedOutPrices = yield select(getCashedOutPrices);
    const fundingType = currentBetId
      ? yield select(getCurrentBetFundingType) : null;

    if (currentBetId && cachedOutPrices.get(currentBetId)) {
      yield put(setMessage(
        SUCCESS,
        fundingType === 'PLUS' ? 'CongratulationsOnline' : 'Congratulations',
        null,
        false,
        'congratulations')
      );
    }
  }
}

export function* clearErrorMessages() {
  const actions = [SET_CURRENT_BET, SET_NEXT_BET, SET_PREV_BET, SHOW_LIST];
  while (yield take(actions)) {
    yield put(clearMessage('live_events'));
    yield put(clearMessage('default'));
  }
}

export default function* root() {
  yield all([
    spawn(watchViewEnter),
    spawn(watchWindowVisibility),
    spawn(watchCurrentBet),
    spawn(liveUpdateCashInPrices),
    spawn(showCongratulations),
    spawn(clearErrorMessages)
  ]);
}
/* eslint-enable no-constant-condition */
